import { useCallback, useMemo } from 'react';
import { InfiniteData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Notification from '../shared/components/Notification';
import { NotificationTypes } from '../enums/notificationTypes';
import { QueryKeys } from '../enums/queryKeys';
import GroupManagerService, {
  GroupManagerGroup,
  GroupManagerPage,
  DuplicateUserResponse,
  MoveUserResponse,
  RemoveUserResponse,
  DeleteGroupResponse,
  CreateGroupPayload,
  CreateGroupResponse,
  GroupFileTree,
  EditGroupResponse,
} from '../services/GroupManager/groupManager.service';

const GROUPS_QUERY_KEY = [QueryKeys.GET_GROUP_MANAGER_GROUPS];
const META_GROUPS_QUERY_KEY = [QueryKeys.GET_GROUP_MANAGER_META_GROUPS];
type GroupCache = InfiniteData<GroupManagerPage, number>;
type GroupUpdater = (group: GroupManagerGroup) => GroupManagerGroup;

// Updater must return the same group reference when nothing changed, so
// memoized group panels don't re-render for unrelated cache patches.
const updateGroupsCache = (cache: GroupCache | undefined, updater: GroupUpdater): GroupCache | undefined => {
  if (!cache) return cache;
  return {
    ...cache,
    pages: cache.pages.map((page) => ({ ...page, groups: page.groups.map(updater) })),
  };
};

const notifySuccess = (message: string) => Notification({ message, type: NotificationTypes.SUCCESS });
// Every mutationFn in groupManager.service.ts throws a normalized GroupManagerError, so error is always an Error here.
const notifyError = (error: unknown) => Notification({ message: (error as Error).message, type: NotificationTypes.ERROR });

export const useGroups = (limit = 20) => {
  const { getGroupsList } = GroupManagerService();
  const query = useInfiniteQuery(getGroupsList(limit));
  const groups = useMemo(() => query.data?.pages.flatMap((page) => page.groups) ?? [], [query.data]);
  const refresh = useCallback(() => query.refetch({ cancelRefetch: true }), [query]);
  const loadMore = useCallback(() => query.fetchNextPage(), [query]);

  return {
    ...query,
    groups,
    hasMore: query.hasNextPage ?? false,
    loadMore,
    refresh,
  };
};

// The full, unpaginated group list backing the duplicate/move target pickers.
export const useGroupOptions = () => {
  const { getGroupOptions } = GroupManagerService();
  const { data, isLoading, isLoadingError } = useQuery(getGroupOptions());

  const groupOptions = useMemo(() => data ?? [], [data]);

  return {
    groupOptions,
    isLoadingGroupOptions: isLoading,
    isGroupOptionsLoadingError: isLoadingError,
  };
};

// For changes made outside Group Manager's own mutations (e.g. editing a member
// in the shared user form) that can rename users or add groups.
export const useRefreshGroupManager = () => {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: GROUPS_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: META_GROUPS_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_GROUP_MANAGER_META_USERS] });
  }, [queryClient]);
};

// Pickers for the create-group form; only fetched while the form is open.
export const useGroupUserOptions = (enabled = true) => {
  const { getUserOptions } = GroupManagerService();
  const { data, isLoading, isLoadingError } = useQuery({ ...getUserOptions(), enabled });

  const userOptions = useMemo(() => data ?? [], [data]);

  return {
    userOptions,
    isLoadingUserOptions: isLoading,
    isUserOptionsLoadingError: isLoadingError,
  };
};

export const useGroupIcons = (enabled = true) => {
  const { getGroupIcons } = GroupManagerService();
  const { data, isLoading, isLoadingError } = useQuery({ ...getGroupIcons(), enabled });

  const groupIcons = useMemo(() => data ?? [], [data]);

  return {
    groupIcons,
    isLoadingGroupIcons: isLoading,
    isGroupIconsLoadingError: isLoadingError,
  };
};

export const useGroupMapOptions = (enabled = true) => {
  const { getMapOptions } = GroupManagerService();
  const { data, isLoading, isLoadingError } = useQuery({ ...getMapOptions(), enabled });

  const mapOptions = useMemo(() => data ?? [], [data]);

  return {
    mapOptions,
    isLoadingMapOptions: isLoading,
    isMapOptionsLoadingError: isLoadingError,
  };
};

const EMPTY_FILE_TREE: GroupFileTree = { folders: [], files: [] };

export const useGroupFileTree = (enabled = true) => {
  const { getFileTree } = GroupManagerService();
  const { data, isLoading, isLoadingError } = useQuery({ ...getFileTree(), enabled });

  return {
    fileTree: data ?? EMPTY_FILE_TREE,
    isLoadingFileTree: isLoading,
    isFileTreeLoadingError: isLoadingError,
  };
};

// The new group's page position depends on server ordering, so refetch rather than patching the cache.
export const useCreateGroup = () => {
  const queryClient = useQueryClient();
  const { createGroup } = GroupManagerService();
  const mutation = useMutation(createGroup());
  const { mutateAsync } = mutation;

  const execute = useCallback(async (payload: CreateGroupPayload): Promise<CreateGroupResponse> => {
    try {
      const response = await mutateAsync(payload);
      void queryClient.invalidateQueries({ queryKey: GROUPS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: META_GROUPS_QUERY_KEY });
      notifySuccess(response.message || 'Group created successfully.');
      return response;
    } catch (error) {
      notifyError(error);
      throw error;
    }
  }, [mutateAsync, queryClient]);

  return { ...mutation, execute };
};

// Loads a group for the edit form; disabled until a group is being edited.
export const useGroupDetails = (groupId: number | undefined) => {
  const { getGroupDetails } = GroupManagerService();
  const { data, isFetching, isError } = useQuery({ ...getGroupDetails(groupId ?? 0), enabled: groupId !== undefined });

  return {
    groupDetails: data,
    // True while a cached copy is being refreshed too, so the form waits for the latest version.
    isFetchingGroupDetails: isFetching,
    isGroupDetailsError: isError,
  };
};

export const useEditGroup = () => {
  const queryClient = useQueryClient();
  const { editGroup } = GroupManagerService();
  const mutation = useMutation(editGroup());
  const { mutateAsync } = mutation;

  const execute = useCallback(async (groupId: number, payload: CreateGroupPayload): Promise<EditGroupResponse> => {
    try {
      const response = await mutateAsync({ groupId, ...payload });
      void queryClient.invalidateQueries({ queryKey: GROUPS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: META_GROUPS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_GROUP_MANAGER_GROUP_DETAILS, groupId] });
      notifySuccess(response.message || 'Group updated successfully.');
      return response;
    } catch (error) {
      notifyError(error);
      throw error;
    }
  }, [mutateAsync, queryClient]);

  return { ...mutation, execute };
};

export const useDeleteGroup = (onAffectedGroup?: (groupId: number) => void) => {
  const queryClient = useQueryClient();
  const { deleteGroup } = GroupManagerService();
  const mutation = useMutation(deleteGroup());
  const { mutateAsync } = mutation;

  const execute = useCallback(async (groupId: number): Promise<DeleteGroupResponse> => {
    try {
      const response = await mutateAsync({ groupId });
      queryClient.setQueryData<GroupCache>(GROUPS_QUERY_KEY, (cache) => {
        if (!cache) return cache;
        return {
          ...cache,
          pages: cache.pages.map((page) => ({
            ...page,
            groups: page.groups.filter((group) => group.id !== groupId),
            pagination: { ...page.pagination, total: Math.max(0, page.pagination.total - 1) },
          })),
        };
      });
      void queryClient.invalidateQueries({ queryKey: GROUPS_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: META_GROUPS_QUERY_KEY });
      onAffectedGroup?.(groupId);
      notifySuccess(response.message || 'Group deleted successfully.');
      return response;
    } catch (error) {
      notifyError(error);
      throw error;
    }
  }, [mutateAsync, onAffectedGroup, queryClient]);

  return { ...mutation, execute };
};

export const useRemoveUserFromGroup = (onAffectedGroup?: (groupId: number) => void) => {
  const queryClient = useQueryClient();
  const { removeUserFromGroup } = GroupManagerService();
  const mutation = useMutation(removeUserFromGroup());
  const { mutateAsync } = mutation;

  const execute = useCallback(async ({ groupId, userId }: { groupId: number; userId: number }): Promise<RemoveUserResponse> => {
    try {
      const response = await mutateAsync({ groupId, userId });
      queryClient.setQueryData<GroupCache>(GROUPS_QUERY_KEY, (cache) => updateGroupsCache(cache, (group) => {
        if (group.id === groupId) return { ...group, members: group.members.filter((member) => member.id !== userId) };
        if (!group.members.some((member) => member.id === userId)) return group;
        return {
          ...group,
          members: group.members.map((member) => member.id === userId
            ? { ...member, groups: member.groups.filter((reference) => reference.id !== groupId) }
            : member),
        };
      }));
      void queryClient.invalidateQueries({ queryKey: GROUPS_QUERY_KEY });
      onAffectedGroup?.(groupId);
      notifySuccess(response.message || 'User removed from group successfully.');
      return response;
    } catch (error) {
      notifyError(error);
      throw error;
    }
  }, [mutateAsync, onAffectedGroup, queryClient]);

  return { ...mutation, execute };
};

// Duplicating is restricted to a single target group at a time, matching the UI.
export const useDuplicateUser = (onAffectedGroups?: (groupIds: number[]) => void) => {
  const queryClient = useQueryClient();
  const { duplicateUser } = GroupManagerService();
  const mutation = useMutation(duplicateUser());
  const { mutateAsync } = mutation;

  const execute = useCallback(async ({ userId, targetGroupId, sourceGroupId }: { userId: number; targetGroupId: number; sourceGroupId: number }): Promise<DuplicateUserResponse> => {
    try {
      const response = await mutateAsync({ userId, groupIds: [targetGroupId] });
      queryClient.setQueryData<GroupCache>(GROUPS_QUERY_KEY, (cache) => {
        const sourceMember = cache?.pages.flatMap((page) => page.groups).find((group) => group.id === sourceGroupId)?.members.find((member) => member.id === userId);
        if (!sourceMember) return cache;
        return updateGroupsCache(cache, (group) => {
          if (group.id !== targetGroupId) return group;
          if (group.members.some((member) => member.id === userId)) return group;
          const memberReference = { id: group.id, name: group.name };
          return {
            ...group,
            members: [...group.members, { ...sourceMember, groups: [...sourceMember.groups, memberReference] }],
          };
        });
      });
      void queryClient.invalidateQueries({ queryKey: GROUPS_QUERY_KEY });
      onAffectedGroups?.(response.addedToGroupIds);
      notifySuccess(response.message || 'User added to selected groups successfully.');
      return response;
    } catch (error) {
      notifyError(error);
      throw error;
    }
  }, [mutateAsync, onAffectedGroups, queryClient]);

  return { ...mutation, execute };
};

// Cache reconciliation for an arbitrary source->targets move was error-prone
// (it could clobber a member's unrelated group memberships); a refetch after
// the move keeps the list correct with much less code.
export const useMoveUser = (onAffectedGroups?: (groupIds: number[]) => void) => {
  const queryClient = useQueryClient();
  const { moveUser } = GroupManagerService();
  const mutation = useMutation(moveUser());
  const { mutateAsync } = mutation;

  const execute = useCallback(async ({ groupId, userId, targetGroupIds }: { groupId: number; userId: number; targetGroupIds: number[] }): Promise<MoveUserResponse> => {
    try {
      const response = await mutateAsync({ groupId, userId, targetGroupIds });
      await queryClient.invalidateQueries({ queryKey: GROUPS_QUERY_KEY });
      onAffectedGroups?.(response.addedToGroupIds);
      notifySuccess(response.message || 'User moved successfully.');
      return response;
    } catch (error) {
      notifyError(error);
      throw error;
    }
  }, [mutateAsync, onAffectedGroups, queryClient]);

  return { ...mutation, execute };
};
